import React, {createContext, useContext, useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {singlePageLocation} from 'util/locationUtil';
import {getCommentsRequest} from 'util/api';
import TrendingCalculator from 'util/trendingCalculator';
import {WebSocketContext} from 'wss/WebSocketService';

const calculateTotalComments = (commentsList) => {
    let total = 0;
    const traverse = (comments) => {
        comments.forEach((comment) => {
            total++;
            if (comment.commentResponses.length > 0) {
                traverse(comment.commentResponses);
            }
        });
    };
    traverse(commentsList);
    return total;
};

// Create the context
export const CommentContext = createContext();

const initialCommentsCount = 8;

const channelName = 'comment';

// Create the provider component
export const CommentStateProvider = ({children}) => {
    const [comments, setComments] = useState(null); // Set initial state to null for loading
    const location = useLocation();
    const [imageId, setImageId] = useState(null);
    const imageIdRef = useRef(null);
    const wsService = useContext(WebSocketContext);
    const isWsBind = useRef(false);
    const isOnMessageBind = useRef(false);

    const totalComments = comments ? calculateTotalComments(comments) : 0; // Adjust for loading state

    useEffect(() => {
        // Fetch comments based on the current URL
        const fetchComments = async () => {
            if ('image' !== singlePageLocation(location.pathname)) {
                setImageId(null);
                imageIdRef.current = null;
            }
        };

        fetchComments();
    }, [location.pathname]);

    useEffect(() => {
        imageIdRef.current = imageId;
        setComments(null); // Set to null when imageId changes to indicate loading
        if (!imageId) {
            return;
        }
        getCommentsRequest(imageId).then((fetchedComments) => {
            const currentTime = Date.now();
            const commentsWithTrending = fetchedComments.map((comment) => ({
                ...comment,
                trending: TrendingCalculator.calculateCommentTrending(comment.reactions, currentTime),
                commentResponses: [],
            }));
            const sortedComments = buildCommentTree(commentsWithTrending);

            // Collect all comments into a flat array
            const allComments = [];
            const collectComments = (commentsList) => {
                commentsList.forEach((comment) => {
                    allComments.push(comment);
                    if (comment.commentResponses && comment.commentResponses.length > 0) {
                        collectComments(comment.commentResponses);
                    }
                });
            };
            collectComments(sortedComments);

            // Sort all comments by trendingSum in descending order
            sortCommentsByCriteria(allComments);

            // Assign 'order' to the top comments
            const topComments = allComments.slice(0, initialCommentsCount);
            topComments.forEach((comment, index) => {
                comment.order = index + 1; // Assign order starting from 1
            });

            // Recalculate trendingSum and sort comments
            recalculateTrendingSum(sortedComments);
            sortCommentsByCriteria(sortedComments);

            setComments(sortedComments); // Set fetched comments to state
        });
    }, [imageId]);

    useEffect(() => {
        wsService.connect();

        const handleMessage = (content, topic) => {
            console.log('Received message on comment channel:', content);
            const currentTime = Date.now();
            const trending = TrendingCalculator.calculateCommentTrending(content.reactions, currentTime);

            if (topic === 'commentUpdated') {
                if (content.pictureId !== imageIdRef.current) {
                    return;
                }
                setComments((prevComments) => {
                    if (!prevComments) {
                        return prevComments;
                    }
                    const updatedComments = updateCommentInTree(prevComments, content);
                    recalculateTrendingSum(updatedComments);
                    sortCommentsByCriteria(updatedComments);
                    return [...updatedComments];
                });
            } else if (topic === 'commentCreated') {
                if (content.pictureId !== imageIdRef.current) {
                    return;
                }
                setComments((prevComments) => {
                    if (!prevComments) return prevComments; // Prevent interaction during loading
                    const totalComments = prevComments.length;

                    const newComment = {
                        ...content,
                        trending,
                        commentResponses: [],
                    };

                    if (content.responseTo) {
                        // This is a response
                        newComment.order = -(
                            prevComments.reduce((responseCount, comment) => {
                                if (comment.id === content.responseTo) {
                                    return responseCount + comment.commentResponses.length;
                                }
                                return responseCount;
                            }, 0) + totalComments
                        );

                        return addCommentResponse(prevComments, newComment);
                    } else {
                        // This is a top-level comment
                        newComment.order = -(totalComments + 1);

                        // Prepend new comment to the top-level comments array
                        const updatedComments = [newComment, ...prevComments];
                        recalculateTrendingSum(updatedComments); // Recalculate totals
                        sortCommentsByCriteria(updatedComments); // Sort comments
                        return updatedComments;
                    }
                });
            }
        };


        if (wsService && imageId && !isWsBind.current) {
            wsService.bindChannel(channelName);
            isWsBind.current = true;
        } else if (wsService && !imageId && isWsBind.current) {
            wsService.unbindChannel(channelName);
            isWsBind.current = false;
        }
        if (wsService && !isOnMessageBind.current) {
            wsService.onMessage(channelName, handleMessage);
            isOnMessageBind.current = true;
        }
    }, [wsService, imageId]);

    return (
        <CommentContext.Provider value={{comments, setImageId, totalComments}}>
            {children}
        </CommentContext.Provider>
    );
};

// Helper function to build the comment tree and calculate trendingSum, totalResponses, and depth
const buildCommentTree = (comments) => {
    const commentMap = {};
    comments.forEach((comment) => {
        comment.commentResponses = [];
        commentMap[comment.id] = comment;
    });

    // Build the tree and set parent references
    comments.forEach((comment) => {
        if (comment.responseTo) {
            const parentComment = commentMap[comment.responseTo];
            if (parentComment) {
                parentComment.commentResponses.push(comment);
                comment.parent = parentComment; // Set parent reference
            } else {
                console.warn(`Parent comment with ID ${comment.responseTo} not found.`);
            }
        } else {
            comment.parent = null; // Top-level comment
        }
    });

    // Get top-level comments
    const topLevelComments = comments.filter((comment) => !comment.responseTo);

    // Calculate trendingSum, totalResponses, and depth starting from the top-level comments
    recalculateTrendingSum(topLevelComments);

    // Sort comments
    sortCommentsByCriteria(topLevelComments);

    return topLevelComments;
};

// Helper function to calculate trendingSum, totalResponses, and depth recursively (bottom-up)
const calculateTrendingSum = (comment, depth = 1) => {
    comment.depth = depth; // Set the depth attribute

    // If the comment has an 'order', skip recalculation
    if (comment.order != null) {
        return;
    }

    // Initialize the sums
    let childrenTrendingSum = 0;
    let childrenTotalResponses = 0;

    // Recurse for child comments
    if (comment.commentResponses.length > 0) {
        comment.commentResponses.forEach((childComment) => {
            calculateTrendingSum(childComment, depth + 1);
            childrenTrendingSum += childComment.trendingSum;
            childrenTotalResponses += 1 + (childComment.totalResponses || 0);
        });
    }

    // Update trendingSum and totalResponses
    comment.trendingSum = comment.trending + childrenTrendingSum;
    comment.totalResponses = childrenTotalResponses;
};

// Helper function to recalculate trendingSum and totalResponses for the entire tree
const recalculateTrendingSum = (commentsList) => {
    commentsList.forEach((comment) => {
        calculateTrendingSum(comment);
    });
};

const sortCommentsByCriteria = (commentsList) => {
    commentsList.sort((a, b) => {
        if (a.isPinned && !b.isPinned) {
            return -1;
        } else if (!a.isPinned && b.isPinned) {
            return 1;
        }

        if (a.order != null && b.order != null) {
            return a.order - b.order;
        } else if (a.order != null) {
            return -1;
        } else if (b.order != null) {
            return 1;
        }

        return b.trendingSum - a.trendingSum;
    });

    commentsList.forEach((comment) => {
        if (comment.commentResponses.length > 0) {
            sortCommentsByCriteria(comment.commentResponses);
        }
    });
};


// Helper function to update a comment in the tree
const updateCommentInTree = (commentsList, updatedComment) => {
    return commentsList.map((comment) => {
        if (comment.id === updatedComment.id) {
            // Update the comment
            return {...comment, ...updatedComment};
        } else if (comment.commentResponses.length > 0) {
            const updatedResponses = updateCommentInTree(comment.commentResponses, updatedComment);
            return {...comment, commentResponses: updatedResponses};
        } else {
            return comment;
        }
    });
};

const addCommentResponse = (commentsList, newComment) => {
    return commentsList.map((comment) => {
        if (comment.id === newComment.responseTo) {
            // Add the new comment as a response
            newComment.parent = comment; // Set parent reference
            const updatedResponses = [...comment.commentResponses, newComment];
            return {...comment, commentResponses: updatedResponses};
        } else if (comment.commentResponses.length > 0) {
            const updatedResponses = addCommentResponse(comment.commentResponses, newComment);
            return {...comment, commentResponses: updatedResponses};
        } else {
            return comment;
        }
    });
};
